import { apiClient as baseApiClient } from '#/api/client.tsx';
import type {
  HighwayMaintenanceFeePenaltyTicket,
  HighwayMaintenanceFeePenaltyTicketOperatorNote,
  HighwayMaintenanceFeePenaltyTicketState,
  HighwayMaintenanceFeePenaltyTicketSummary,
} from '../model';
import { format } from 'date-fns';

const hmfpApiClient = baseApiClient.create({
  baseURL: `${import.meta.env.VITE_BASE_URL}/cockpit/auto-pay`,
});

const TICKETS_BASE = '/highway-maintenance-fee-penalty-auto-pay-tickets';

type ISODateTime = string;

type ListTicketsParams = {
  page?: number;
  pageSize?: number;
  state?: HighwayMaintenanceFeePenaltyTicketState[];
  accountEmail?: string;
  plateNumber?: string;
  idNumber?: string;
  taxIdNumber?: string;
  createDateTimeFrom?: ISODateTime;
  createDateTimeTo?: ISODateTime;
};

type CompleteTicketBody = {
  completeDateTime: ISODateTime;
};

// --- Response types ---

type ChargeTicketResponse = {
  id: string;
  state: string;
  paymentId: string | null;
};

type TicketStateChangeResponse = Pick<
  HighwayMaintenanceFeePenaltyTicket,
  'id' | 'state'
>;

// --- Endpoints ---

type ListTicketsResponse = {
  data: HighwayMaintenanceFeePenaltyTicketSummary[];
  extra: {
    totalElement: number;
    totalPage: number;
  };
};

// === info getter ===
export const listTickets = (
  params: ListTicketsParams = {} as ListTicketsParams,
) => {
  return hmfpApiClient
    .get<ListTicketsResponse>(`${TICKETS_BASE}`, {
      params,
      paramsSerializer(params) {
        const cleaned = Object.fromEntries(
          Object.entries(params).filter(([, value]) => value !== undefined),
        );
        const urlParams = new URLSearchParams(cleaned);
        return urlParams.toString();
      },
    })
    .then((res) => res.data);
};

export const getTicketById = (id: string) => {
  return hmfpApiClient
    .get<{ data: HighwayMaintenanceFeePenaltyTicket }>(`${TICKETS_BASE}/${id}`)
    .then((res) => res.data.data);
};

type ExportTicketsParams = {
  from?: ISODateTime;
  to?: ISODateTime;
};

type ExportCompletedTicketParams = Required<ExportTicketsParams>;

export const exportCompletedTicket = (params: ExportCompletedTicketParams) => {
  return hmfpApiClient
    .get<Blob>(`${TICKETS_BASE}/excel`, {
      params: { state: 'COMPLETED', ...params },
      responseType: 'blob',
    })
    .then((res) => {
      const currentTimestamp = format(new Date(), 'HHmmss');
      const startDate = format(new Date(params.from), 'yyyyMMdd');
      const endDate = format(new Date(params.to), 'yyyyMMdd');
      const filename = `公路養管費罰鍰_${startDate}-${endDate}_${currentTimestamp}`;
      return {
        blob: res.data,
        filename,
      };
    });
};

// === finish the query of the ticket ===

type FailedMutationBody = {
  reason: string;
};

export const markNoFee = (id: string, body: FailedMutationBody) =>
  hmfpApiClient
    .post<{
      data: TicketStateChangeResponse;
    }>(`${TICKETS_BASE}/${id}/no-fee`, body)
    .then((res) => res.data.data);

export const markQueryFailed = (id: string, body: FailedMutationBody) =>
  hmfpApiClient
    .post<{
      data: TicketStateChangeResponse;
    }>(`${TICKETS_BASE}/${id}/query-failed`, body)
    .then((res) => res.data.data);

type ChargeTicketBody = {
  amount: number;
  handlingFee: number;
};

export const chargeTicket = (id: string, body: ChargeTicketBody) =>
  hmfpApiClient
    .post<{ data: ChargeTicketResponse }>(`${TICKETS_BASE}/${id}/charge`, body)
    .then((res) => res.data.data);

export const completeTicket = (id: string, body: CompleteTicketBody) =>
  hmfpApiClient
    .post<{
      data: TicketStateChangeResponse;
    }>(`${TICKETS_BASE}/${id}/complete`, body)
    .then((res) => res.data.data);

type AppendOperatorNoteBody = Pick<
  HighwayMaintenanceFeePenaltyTicketOperatorNote,
  'content'
>;

type AddOperatorNoteResponse = HighwayMaintenanceFeePenaltyTicketOperatorNote;

export const appendOperatorNote = (id: string, body: AppendOperatorNoteBody) =>
  hmfpApiClient
    .post<{
      data: AddOperatorNoteResponse;
    }>(`${TICKETS_BASE}/${id}/operator-notes`, body)
    .then((res) => res.data.data);

export type {
  ListTicketsParams,
  ExportTicketsParams,
  ExportCompletedTicketParams,
  ChargeTicketBody,
  CompleteTicketBody,
  AppendOperatorNoteBody as AddOperatorNoteBody,
  ListTicketsResponse,
  ChargeTicketResponse,
  TicketStateChangeResponse,
  AddOperatorNoteResponse,
};
